import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function StockInstantane() {
    const navigate = useNavigate();
    const [stock, setStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [filterEtat, setFilterEtat] = useState('');
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [sortConfig, setSortConfig] = useState({ key: 'designation', direction: 'asc' });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchStock();
        fetchStats();
        fetchCategories();
    }, [filterCategorie, filterEtat]);

    const fetchStock = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/stock_etat.php?action=instantane`;
            
            const params = new URLSearchParams();
            if (filterCategorie) params.append('categorie', filterCategorie);
            if (filterEtat) params.append('etat', filterEtat);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setStock(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/categories.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const getEtatBadge = (etat) => {
        const configs = {
            rupture: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Rupture' },
            critique: { color: '#f59e0b', icon: 'ri-alert-line', label: 'Critique' },
            faible: { color: '#0ea5e9', icon: 'ri-information-line', label: 'Faible' },
            normal: { color: '#10b981', icon: 'ri-check-line', label: 'Normal' }
        };
        const config = configs[etat] || configs.normal;
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const getStockStatus = (quantite, seuil) => {
        if (quantite <= 0) return { color: '#ef4444', value: 0, label: 'Rupture' };
        if (quantite <= seuil) return { color: '#f59e0b', value: (quantite / seuil) * 50, label: 'Critique' };
        if (quantite <= seuil * 2) return { color: '#0ea5e9', value: 50 + ((quantite - seuil) / seuil) * 25, label: 'Faible' };
        return { color: '#10b981', value: 75 + Math.min(((quantite - seuil * 2) / seuil) * 25, 25), label: 'Normal' };
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredStock = stock
        .filter(p => 
            p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            p.designation?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'designation') {
                return sortConfig.direction === 'asc' 
                    ? a.designation?.localeCompare(b.designation)
                    : b.designation?.localeCompare(a.designation);
            }
            if (sortConfig.key === 'quantite_reelle') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite_reelle || 0) - (b.quantite_reelle || 0)
                    : (b.quantite_reelle || 0) - (a.quantite_reelle || 0);
            }
            if (sortConfig.key === 'valeur') {
                const valeurA = (a.quantite_reelle || 0) * (a.prix_achat_ht || 0);
                const valeurB = (b.quantite_reelle || 0) * (b.prix_achat_ht || 0);
                return sortConfig.direction === 'asc' ? valeurA - valeurB : valeurB - valeurA;
            }
            return 0;
        });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStock.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredStock.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalValeurStock = filteredStock.reduce((sum, p) => 
        sum + ((p.quantite_reelle || 0) * (p.prix_achat_ht || 0)), 0
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du stock...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchStock}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-stack-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Stock instantané</h4>
                                    <p className="text-muted small mb-0">
                                        {filteredStock.length} produits • {formatCurrency(totalValeurStock)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                className="rounded-pill px-3"
                                style={viewMode === 'table' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-table-line me-1"></i>Tableau
                            </Button>
                            <Button 
                                variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('cards')}
                                className="rounded-pill px-3"
                                style={viewMode === 'cards' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-grid-line me-1"></i>Cartes
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/produits')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-stack-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total produits</span>
                                        <h3 className="kpi-value">{stats.global?.total_produits || 0}</h3>
                                        <span className="kpi-unit">en stock</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-close-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Rupture</span>
                                        <h3 className="kpi-value">{stats.global?.produits_rupture || 0}</h3>
                                        <span className="kpi-unit">à réapprovisionner</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-alert-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Stock critique</span>
                                        <h3 className="kpi-value">{stats.global?.produits_critiques || 0}</h3>
                                        <span className="kpi-unit">sous seuil d'alerte</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-money-euro-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Valeur stock</span>
                                        <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(stats.global?.valeur_achat || 0)}</h3>
                                        <span className="kpi-unit">prix d'achat</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={4}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterCategorie}
                                        onChange={(e) => setFilterCategorie(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value=""> Toutes catégories</option>
                                        {categories.map(c => (
                                            <option key={c.id_categorie} value={c.id_categorie}>
                                                {c.nom_categorie}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterEtat}
                                        onChange={(e) => setFilterEtat(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value=""> Tous états</option>
                                        <option value="rupture"> Rupture</option>
                                        <option value="critique"> Stock critique</option>
                                        <option value="faible"> Stock faible</option>
                                        <option value="normal"> Stock normal</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => {
                                            setFilterText('');
                                            setFilterCategorie('');
                                            setFilterEtat('');
                                        }} 
                                        className="w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                                    >
                                        <i className="ri-refresh-line"></i>
                                        <span>Réinitialiser</span>
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'asc' })}
                                        className="rounded-pill"
                                    >
                                        <option value="designation">Trier par nom</option>
                                        <option value="quantite_reelle">Trier par quantité</option>
                                        <option value="valeur">Trier par valeur</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="designation">Produit</SortableHeader>
                                                <th>Catégorie</th>
                                                <SortableHeader field="quantite_reelle">Stock</SortableHeader>
                                                <th>Seuil</th>
                                                <th>Disponible</th>
                                                <SortableHeader field="valeur">Valeur</SortableHeader>
                                                <th>État</th>
                                                <th className="text-center pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((p) => {
                                                    const status = getStockStatus(p.quantite_reelle, p.seuil_alerte);
                                                    return (
                                                        <tr key={p.id_produit} className="stock-row">
                                                            <td>
                                                                <Link to={`/stock/produits/${p.id_produit}`} className="table-link">
                                                                    {p.designation}
                                                                </Link>
                                                                {p.emplacement && (
                                                                    <div>
                                                                        <small className="text-muted">
                                                                            <i className="ri-map-pin-line me-1 fs-10"></i>
                                                                            {p.emplacement}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>{p.nom_categorie || <span className="text-muted">-</span>}</td>
                                                            <td style={{ minWidth: '140px' }}>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>{status.label}</Tooltip>}>
                                                                        <div className="progress-bar-custom" style={{ width: '80px' }}>
                                                                            <div className="progress-fill" style={{ width: `${status.value}%`, background: status.color }}></div>
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                    <span className={`fw-semibold ${status.color === '#ef4444' ? 'text-danger' : ''}`}>
                                                                        {p.quantite_reelle} {p.unite_mesure}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge className="rounded-pill px-3" style={{ background: '#64748b' }}>
                                                                    {p.seuil_alerte}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center fw-bold text-success">
                                                                {p.quantite_disponible || 0}
                                                            </td>
                                                            <td className="fw-bold text-primary">
                                                                {formatCurrency((p.quantite_reelle || 0) * (p.prix_achat_ht || 0))}
                                                            </td>
                                                            <td>{getEtatBadge(p.etat_stock)}</td>
                                                            <td className="text-center pe-4">
                                                                <div className="d-flex gap-2 justify-content-center">
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                        <Button 
                                                                            variant="outline-info" 
                                                                            size="sm"
                                                                            className="action-btn"
                                                                            onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                        >
                                                                            <i className="ri-eye-line"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter au stock</Tooltip>}>
                                                                        <Button 
                                                                            variant="outline-success" 
                                                                            size="sm"
                                                                            className="action-btn"
                                                                            onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}`)}
                                                                        >
                                                                            <i className="ri-add-line"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-inbox-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucun produit trouvé</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || filterCategorie || filterEtat 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Le stock est vide"}
                                                            </p>
                                                            {(filterText || filterCategorie || filterEtat) && (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setFilterCategorie('');
                                                                        setFilterEtat('');
                                                                    }}
                                                                    className="rounded-pill"
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredStock.length)} sur {filteredStock.length} produits
                                        </div>
                                        <Pagination className="mb-0">
                                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                            {renderPagination()}
                                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((p) => {
                                        const status = getStockStatus(p.quantite_reelle, p.seuil_alerte);
                                        return (
                                            <Col lg={6} xl={4} key={p.id_produit}>
                                                <Card className="border-0 shadow-sm rounded-3 h-100 stock-card">
                                                    <Card.Body className="p-4">
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <Badge className="mb-2 px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                                                    {p.reference}
                                                                </Badge>
                                                                <h5 className="mb-1">{p.designation}</h5>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <Badge bg="light" text="dark" className="rounded-pill">
                                                                        {p.nom_categorie || 'Sans catégorie'}
                                                                    </Badge>
                                                                    {p.emplacement && (
                                                                        <Badge bg="light" text="dark" className="rounded-pill">
                                                                            <i className="ri-map-pin-line me-1"></i>
                                                                            {p.emplacement}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <h4 className="text-primary mb-0">{formatCurrency((p.quantite_reelle || 0) * (p.prix_achat_ht || 0))}</h4>
                                                                <small className="text-muted">valeur stock</small>
                                                            </div>
                                                        </div>

                                                        <div className="mb-4">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <small className="text-muted">Niveau de stock</small>
                                                                <span className="fw-semibold">{p.quantite_reelle} {p.unite_mesure}</span>
                                                            </div>
                                                            <div className="progress-bar-custom" style={{ width: '100%', height: '10px' }}>
                                                                <div className="progress-fill" style={{ width: `${status.value}%`, background: status.color }}></div>
                                                            </div>
                                                            <div className="d-flex justify-content-between mt-2">
                                                                <small className="text-muted">Seuil: {p.seuil_alerte}</small>
                                                                <small className="text-muted">Disponible: {p.quantite_disponible || 0}</small>
                                                            </div>
                                                        </div>

                                                        <hr className="my-3" />

                                                        <div className="d-flex justify-content-between align-items-center">
                                                            {getEtatBadge(p.etat_stock)}
                                                            <div className="d-flex gap-2">
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                    <Button 
                                                                        variant="outline-info" 
                                                                        size="sm"
                                                                        className="action-btn"
                                                                        onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                    >
                                                                        <i className="ri-eye-line"></i>
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter au stock</Tooltip>}>
                                                                    <Button 
                                                                        variant="outline-success" 
                                                                        size="sm"
                                                                        className="action-btn"
                                                                        onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}`)}
                                                                    >
                                                                        <i className="ri-add-line"></i>
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm rounded-3 text-center py-5">
                                            <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                            <h5 className="mt-3">Aucun produit trouvé</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredStock.length)} sur {filteredStock.length} produits
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .stock-row {
                    transition: all 0.2s ease;
                }
                
                .stock-row:hover {
                    background: #f8fafc;
                }
                
                /* Stock Card */
                .stock-card {
                    transition: all 0.3s ease;
                }
                
                .stock-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}